import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { apiClient } from '../apiClient';
import { Mail, Lock, User, Gift } from 'lucide-react';
import BrandMark from '../components/BrandMark';

function InviteSignupPage({ setUser }) {
  const { token } = useParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tier, setTier] = useState('starter');
  const [inviteName, setInviteName] = useState('');
  const [inviteNote, setInviteNote] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const validateToken = async () => {
      try {
        const data = await apiClient.get(`/api/auth/validate-invite/${token}`);
        setTokenValid(true);
        setTier(data.tier || 'starter');
        setInviteName(data.name || '');
        setInviteNote(data.note || '');
      } catch (err) {
        setError(err.message || 'Invalid or expired invite link');
        setTokenValid(false);
      } finally {
        setValidating(false);
      }
    };
    validateToken();
  }, [token]);

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = await apiClient.post('/api/auth/signup', {
        email,
        password,
        name,
        invite_token: token
      });
      setUser(data.user);
      navigate('/profile?edit=1');
    } catch (err) {
      setError(err.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-rebel-cyan border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Validating your invite...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card-glass max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-rebel-red/20 flex items-center justify-center mx-auto mb-4">
            <Gift className="w-8 h-8 text-rebel-red" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Invalid Invite</h1>
          <p className="text-gray-400 mb-6">{error || 'This invite link is invalid or has already been used.'}</p>
          <Link to="/" className="btn-primary">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8 animate-fade-in">
          <div className="flex justify-center mb-4">
            <BrandMark size={72} className="auth-logo" />
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rebel-cyan/20 border border-rebel-cyan/30 mb-4">
            <Gift className="w-4 h-4 text-rebel-cyan" />
            <span className="text-rebel-cyan text-sm font-medium">
              {tier === 'pro' ? 'Pro Access Invite' : 'Free Access Invite'}
            </span>
          </div>
          <h1 className="text-3xl font-bold mb-2 text-gradient">Welcome to Rebel Prompts</h1>
          <p className="text-gray-400">
            {inviteName ? `${inviteName} access is ready.` : "You've been invited. Create your account below."}
          </p>
          {inviteNote && <p className="text-gray-500 mt-2">{inviteNote}</p>}
        </div>

        <div className="card-glass animate-fade-in">
          <form onSubmit={handleSignup} className="space-y-4">
            {error && (
              <div className="error-banner">{error}</div>
            )}

            <div className="modal-field">
              <label>Full Name</label>
              <div className="input-with-icon">
                <User className="icon" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  required
                  className="input-glass"
                />
              </div>
            </div>

            <div className="modal-field">
              <label>Email Address</label>
              <div className="input-with-icon">
                <Mail className="icon" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="input-glass"
                />
              </div>
            </div>

            <div className="modal-field">
              <label>Password</label>
              <div className="input-with-icon">
                <Lock className="icon" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  required
                  minLength={8}
                  className="input-glass"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? 'Creating Account...' : 'Claim Your Access'}
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-4">
            By signing up, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
}

export default InviteSignupPage;
